import { useAnalytics } from "@lazarentertainment/analytics-collector"
import ImagePlaceholder from "assets/icons/imagePlaceholder.svg"
import {
  NumberInputButton,
  NumberInputElement,
} from "client/packages/commerce/components/ui/inputs/styles/numberInputStyles"
import { Price } from "client/packages/commerce/components/ui/priceComponents/Price"
import { ProductTitle } from "client/packages/commerce/components/ui/textStyles"
import { useEffect, useState } from "react"
import { buttonFocus } from "shared/styles/mixins"
import styled from "styled-components"

import { useChannelContext } from "../../../../components/channel"
import { useVariantFromCache } from "../../hooks"

import {
  MAX_QUANTITY,
  TargetCartItem,
  useTargetCartContext,
} from "./TargetCartContext"

export const ItemWrapper = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${(props) => props.theme.input.borderColor};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`

export const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
`

export const ImageWrapper = styled.div`
  height: 60px;
  width: 100px;
  position: relative;

  svg {
    max-width: 100%;
    max-height: 100%;
  }
`

export const Titles = styled.div`
  text-align: left;
  margin-left: 10px;
  width: calc(100% - 100px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${ProductTitle} {
    font-family: "Target", Helvetica, sans-serif;
  }
`

export const PriceWrapper = styled.div`
  margin-left: auto;
  font-size: 1.2rem;
  font-family: "Target", Helvetica, sans-serif;
`

const Bottom = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  &::not(:last-child) {
    margin-bottom: 5px;
  }
`

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
`

const NumberInput = styled(NumberInputElement)`
  padding: 6px;
  width: 32px;
  height: 32px;

  * {
    font-family: "Target", Helvetica, sans-serif !important;
  }
`

const NumButton = styled(NumberInputButton)`
  padding: 6px;
  width: 32px;
  height: 32px;

  &:first-of-type {
    border-radius: 6px 0 0 6px;
  }

  &:last-of-type {
    border-radius: 0 6px 6px 0;
  }
`

const RemoveButton = styled.button`
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  font-size: 12px;
  background-color: transparent;
  margin-right: auto;
  border-width: ${(props) => props.theme.input.borderWidth};
  border-style: solid;
  border-color: ${(props) => props.theme.input.borderColor};
  border-radius: 2px;
  font-family: "Target", Helvetica, sans-serif;

  ${buttonFocus};
`

export type CartItemProps = {
  cartItem: TargetCartItem
  currencyCode: string
  maxQuantity?: number
}

const CartItemTile = ({
  cartItem,
  currencyCode,
  maxQuantity = MAX_QUANTITY,
}: CartItemProps) => {
  const { analytics } = useAnalytics()
  const { channelId, broadcastId, recordingId } = useChannelContext()
  // FIXME: When #1479 is completed. variant can be null
  const [productVariant, { loading }] = useVariantFromCache(
    cartItem.productId,
    cartItem.variantId,
  )
  const { quantity } = cartItem
  const { removeFromCart, updateQuantity } = useTargetCartContext()
  const totalPrice = parseFloat(productVariant.price) * quantity

  // InputValue is not directly tied to quantity so that the user
  // can manually input values, including clearing the input altogether during
  // input. And then when the input is blurred we will re-sync values and
  // ensure min/max are adhered to.
  const [inputValue, setInputValue] = useState<number | undefined>(
    quantity,
  )
  useEffect(() => {
    setInputValue(quantity)
  }, [quantity])

  if (loading) {
    return null
  }

  const updateCartQuantity = (newQuantity: number) => {
    if (newQuantity !== cartItem.quantity) {
      analytics.track("target_add_to_cart", {
        channelId,
        broadcastId,
        recordingId,
        productId: cartItem.productId,
        variantId: productVariant.id,
        price: productVariant.price,
        quantity: newQuantity - cartItem.quantity,
      })
    }
    updateQuantity(productVariant.id, newQuantity)
  }

  return (
    <ItemWrapper>
      <Top>
        <ImageWrapper>
          {productVariant.featuredImage?.url ? (
            <img
              src={productVariant.featuredImage.url}
              alt={
                productVariant.featuredImage.altText ??
                productVariant.title
              }
            />
          ) : (
            <ImagePlaceholder />
          )}
        </ImageWrapper>
        <Titles>
          <ProductTitle $maxLines={2}>{productVariant.title}</ProductTitle>
          <PriceWrapper>
            <Price
              amount={totalPrice.toString()}
              currencyCode={currencyCode}
              showCurrencyCode={false}
            />
          </PriceWrapper>
        </Titles>
      </Top>

      <Bottom>
        <RemoveButton
          type="button"
          onClick={() => {
            analytics.track("target_add_to_cart", {
              channelId,
              broadcastId,
              recordingId,
              productId: cartItem.productId,
              variantId: cartItem.variantId,
              price: cartItem.price,
              quantity: -cartItem.quantity,
            })
            return removeFromCart(productVariant.id)
          }}
        >
          {" "}
          Remove{" "}
        </RemoveButton>
        <InputWrapper>
          <NumButton
            onClick={() => {
              const newQuantity = Math.min(quantity - 1, maxQuantity)
              updateCartQuantity(newQuantity)
            }}
          >
            -
          </NumButton>
          <NumberInput
            value={inputValue}
            onChange={(event) =>
              setInputValue(
                event.target.value !== ""
                  ? Number(event.target.value)
                  : undefined,
              )
            }
            onBlur={() => {
              const newQuantity = Math.min(inputValue ?? 0, maxQuantity)
              setInputValue(newQuantity)
              updateCartQuantity(newQuantity)
            }}
            max={maxQuantity}
          />
          <NumButton
            onClick={() => {
              const newQuantity = Math.min(quantity + 1, maxQuantity)
              updateCartQuantity(newQuantity)
            }}
          >
            +
          </NumButton>
        </InputWrapper>
      </Bottom>
    </ItemWrapper>
  )
}

export default CartItemTile
